import { Dispatch, FC, SetStateAction, useState } from 'react';
import { generalHelpers, generalHthHelper } from '@monorepo/helpers';
import { hideLoadingModalReducer, setAlertResponse, setValuesAndOpenAlertModalReducer, showLoadingModalReducer } from '@/store';
import { houseToHouseServices, hthHelper } from 'src/services';
import { Hr } from '@/components/_commons';
import { typeHTHBuilding, typeHTHTerritory, typeTerritoryNumber } from '@monorepo/models';
import { useDispatch } from 'react-redux';
import { useGetConfig, useGetIsDarkMode, useGetIsMobile } from 'src/custom-hooks';

type propsType = {
    hthTerritories: typeHTHTerritory[];
};

export const HTHBuildingsForAdmins: FC<propsType> = ({ hthTerritories }) => {
    const [territoryToShow, setTerritoryToShow] = useState<typeHTHTerritory | null>(null);
    const dispatch = useDispatch();
    const isDarkMode = useGetIsDarkMode();

    const resetHouseholdsAll = (): void => {
        dispatch(setValuesAndOpenAlertModalReducer({
            title: 'Resetear timbres de todos los territorios',
            message: `¿Estás seguro que querés resetear los timbres de todos los territorios? Esta acción es irreversible.`,
            animation: 3,
            mode: 'confirm',
            execution: async () => {
                dispatch(showLoadingModalReducer());
                const response = await houseToHouseServices.ResetHouseholdsAll();
                dispatch(hideLoadingModalReducer());
                dispatch(setAlertResponse({ response }));
            }
        }));
    }

    const resetHouseholdsFromBuilding = (building: typeHTHBuilding): void => {
        dispatch(setValuesAndOpenAlertModalReducer({
            title: 'Resetear timbres de este edificio',
            message: `¿Estás seguro que querés resetear los timbres del edificio ${building.street || '?'} ${building.streetNumber}?`,
            animation: 3,
            mode: 'confirm',
            execution: async () => {
                dispatch(showLoadingModalReducer());
                const response = await houseToHouseServices.ResetHouseholdsFromBuilding(building);
                dispatch(hideLoadingModalReducer());
                dispatch(setAlertResponse({ response }));
            }
        }));
    }
    const resetHouseholdsFromTerritory = (territoryNumber: typeTerritoryNumber): void => {
        dispatch(setValuesAndOpenAlertModalReducer({
            title: 'Resetear timbres de este territorio',
            message: `¿Estás seguro que querés resetear los timbres del territorio ${territoryNumber}? Esta acción es irreversible.`,
            animation: 3,
            mode: 'confirm',
            execution: async () => {
                dispatch(showLoadingModalReducer());
                const response = await houseToHouseServices.ResetHouseholdsFromTerritory(territoryNumber);
                dispatch(hideLoadingModalReducer());
                dispatch(setAlertResponse({ response }));
            }
        }));
    }

    return (
        <div className={'mt-4'}>
            <h3 className={`mt-4 ${isDarkMode ? 'text-white' : ''}`}>
                Cantidad total de edificios: {hthTerritories.map(t => t.faces.map(x => x.buildings?.length || 0).reduce((a, n) => a + n, 0)).reduce((a, n) => a + n, 0)}
            </h3>
            <button
                className={'btn btn-general-red btn-sm py-1 px-3 mt-3'}
                onClick={resetHouseholdsAll}
            >
                Resetear timbres de todos los territorios
            </button>
            {hthTerritories.sort((a, b) => parseInt(a.territoryNumber) - parseInt(b.territoryNumber)).map(t =>
                <Territory key={t.territoryNumber}
                    resetHouseholdsFromBuilding={resetHouseholdsFromBuilding}
                    resetHouseholdsFromTerritory={resetHouseholdsFromTerritory}
                    setTerritoryToShow={setTerritoryToShow}
                    t={t}
                    territoryToShow={territoryToShow}
                />
            )}
        </div>
    );
}

type propsType1 = {
    resetHouseholdsFromBuilding: (building: typeHTHBuilding) => void;
    resetHouseholdsFromTerritory: (territoryNumber: typeTerritoryNumber) => void;
    setTerritoryToShow: Dispatch<SetStateAction<typeHTHTerritory | null>>;
    t: typeHTHTerritory;
    territoryToShow: typeHTHTerritory | null;
};

const Territory: FC<propsType1> = ({
    resetHouseholdsFromBuilding, resetHouseholdsFromTerritory, setTerritoryToShow, t, territoryToShow
}) => {
    const config = useGetConfig();
    const isDarkMode = useGetIsDarkMode();
    const isMobile = useGetIsMobile();

    return (
        <div className={'my-5'}>
            <div className={`card ${isDarkMode ? 'text-white' : ''}`}>
                <div className={`card-header ${isDarkMode ? 'bg-success' : ''} d-flex justify-content-between`}>
                    <div className={'fw-bold'}>
                        TERRITORIO {t.territoryNumber} - Edificios: {t.faces.map(x => x.buildings?.length || 0).reduce((a, n) => a + n, 0)}
                    </div>
                    <div>
                        {!isMobile && t.territoryNumber === territoryToShow?.territoryNumber &&
                            <button
                                className={'btn btn-general-red btn-sm py-1 px-3 me-4'}
                                onClick={() => resetHouseholdsFromTerritory(t.territoryNumber)}
                            >
                                Resetear timbres del territorio {t.territoryNumber}
                            </button>
                        }
                        <button className={`btn btn-general-${t.territoryNumber === territoryToShow?.territoryNumber ? 'red' : 'blue'} btn-sm p-1`}
                            style={{ width: '90px' }}
                            onClick={() => setTerritoryToShow(x => x?.territoryNumber === t.territoryNumber ? null : t)}
                        >
                            {t.territoryNumber === territoryToShow?.territoryNumber ? 'Ocultar' : 'Ver todo'}
                        </button>
                    </div>
                </div>
                {isMobile && t.territoryNumber === territoryToShow?.territoryNumber &&
                    <div className={isDarkMode ? 'bg-dark' : ''}>
                        <button
                            className={'btn btn-general-red btn-sm p-1 mt-3 ms-3'}
                            onClick={() => resetHouseholdsFromTerritory(t.territoryNumber)}
                        >
                            Resetear timbres de todo el territorio {t.territoryNumber}
                        </button>
                    </div>
                }
                <div className={`card-body ${isDarkMode ? 'bg-dark' : ''}`}>
                    {territoryToShow?.territoryNumber === t.territoryNumber &&
                        <>
                        {t.faces.sort((a, b) => parseInt(a.block) - parseInt(b.block) || a.face.localeCompare(b.face)).map(polygon =>
                            <div key={polygon.id}>
                                {!!polygon.buildings.length &&
                                    <>
                                    {(polygon.face === 'A'
                                        || (polygon.face === 'B' && !t.faces.some(x => x.block === polygon.block && x.face === 'A' && !!x.buildings))
                                        || (polygon.face === 'C' && !t.faces.some(x => x.block === polygon.block && ['A', 'B'].includes(x.face) && !!x.buildings))
                                        || (polygon.face === 'D' && !t.faces.some(x => x.block === polygon.block && ['A', 'B', 'C'].includes(x.face) && !!x.buildings))
                                    ) &&
                                        <>
                                        <Hr />
                                        <h4 className='mt-3 mb-2'>
                                            MANZANA {generalHthHelper.MaskTheBlock(polygon.block, config.isUsingLettersForBlocks)} - Edificios: {t.faces.filter(p => p.block === polygon.block).map(p => p.buildings?.length || 0).reduce((a, n) => a + n, 0)}
                                        </h4>
                                        </>
                                    }
                                    {polygon.buildings?.sort((a, b) => a.streetNumber - b.streetNumber).map(building =>
                                        <div key={`${polygon.street}_${building.streetNumber}`}>
                                            <Hr />
                                            <h5 >
                                                {polygon.street} {building.streetNumber}{building.streetNumber2 ? `/${building.streetNumber2}` : ''}{building.streetNumber3 ? `/${building.streetNumber3}` : ''}:
                                            </h5>
                                            <ul className={'mb-3'}>
                                                <li>
                                                    {building.households.length} timbres
                                                </li>
                                                <li>
                                                    {config.isEnabledHthDoubleState ?
                                                        `${building.households.filter(h => h.isChecked).length} marcados - ${building.households.filter(h => h.isCheckedNotAnswered).length} no contestaron - ${building.households.filter(h => !h.isChecked && !h.isCheckedNotAnswered).length} sin marcar`
                                                        :
                                                        `${building.households.filter(h => h.isChecked || h.isCheckedNotAnswered).length} marcados - ${building.households.filter(h => !h.isChecked && !h.isCheckedNotAnswered).length} sin marcar`
                                                    }
                                                </li>
                                                <li>
                                                    Últ. vez: {hthHelper.GetLastTimeOfBuilding(building)}
                                                </li>
                                                {!!building.households.some(h => h.history.filter(h => !!h.state).length) &&
                                                    <li>
                                                        Todas las fechas de predicación: {[... new Set(building.households.filter(h => h.history.some(h => !!h.state)).map(h => h.history.filter(h => !!h.state).map(h => h.timestamp)).flat().sort((d1, d2) => d2! - d1!).map(d => generalHelpers.InvertDateString(generalHelpers.FormatDate(new Date(d!)))))].join(' + ')}
                                                    </li>
                                                }
                                            </ul>
                                            <div>
                                                <button
                                                    className={'btn btn-general-red btn-sm py-1 px-2 mb-2 ms-3'}
                                                    onClick={() => resetHouseholdsFromBuilding({ ...building, street: polygon.street, territoryNumber: t.territoryNumber })}
                                                >
                                                    Resetear timbres de este Edificio
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                    </>
                                }
                            </div>
                        )}
                        </>
                    }
                </div>
            </div>
        </div>
    );
}
